import { api } from './api'
import type { PaginationMeta, Poll, PollStatus, VotingUnit } from '@riverpark/types'

export interface ListPollsFilters {
  page?: number
  limit?: number
  status?: PollStatus
}

export async function listPolls(
  filters: ListPollsFilters = {},
): Promise<{ data: Poll[]; meta: PaginationMeta }> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.status) params.set('status', filters.status)
  const query = params.toString()
  return api.get<{ data: Poll[]; meta: PaginationMeta }>(`/polls${query ? `?${query}` : ''}`)
}

export async function getPoll(id: string): Promise<{ data: Poll }> {
  return api.get<{ data: Poll }>(`/polls/${id}`)
}

/** The poll list never carries `votedOptionId` (only single-item fetches do), so this composes
 * a small list of open polls with the per-viewer vote state — a viewer never has more than a
 * handful open at once, so one detail fetch per open poll stays cheap. */
export async function listOpenPollsAwaitingVote(): Promise<Poll[]> {
  const { data: openPolls } = await listPolls({ status: 'open', limit: 20 })
  const detailed = await Promise.all(openPolls.map((poll) => getPoll(poll.id)))
  return detailed.map((res) => res.data).filter((poll) => poll.votedOptionId === null)
}

export interface CreatePollInput {
  title: string
  description?: string
  votingUnit: VotingUnit
  /** ISO datetime strings. */
  startAt: string
  endAt: string
  options: { label: string }[]
}

export async function createPoll(input: CreatePollInput): Promise<{ data: { id: string } }> {
  return api.post<{ data: { id: string } }>('/polls', input)
}

export async function castVote(
  pollId: string,
  optionId: string,
): Promise<{ data: { success: boolean } }> {
  return api.post<{ data: { success: boolean } }>(`/polls/${pollId}/vote`, { optionId })
}

export async function deletePoll(id: string): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(`/polls/${id}`)
}
